/**
 * Sortable, paginated, anonymized table of per-user relationship scores.
 * Drives the Relationship tab's main view. Integrates with the
 * RevealIdentifiersToggle via the `revealed` prop.
 */
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../../lib/cn';
import { formatInt, formatPercent } from '../format';
import { RELATIONSHIP_TIER_COLORS, RELATIONSHIP_TIER_LABEL } from '../theme';

export interface RelationshipUserRow {
  user_token: string;
  score: number;
  tier: string;
  return_days_28d: number;
  avg_chat_msgs_per_day: number;
  recalibrations_28d: number;
  adherence_pct: number;
  memories_count: number;
  // 4 dead engagement signals were removed here (recon 2026-07-04): no longer
  // scored, no longer emitted by the read endpoint, no longer shown in the table.
}

export type RelationshipSortKey =
  | 'relationship_score'
  | 'return_days_28d'
  | 'recalibrations_28d'
  | 'adherence_pct'
  | 'memories_count';

interface ColumnDef {
  key: RelationshipSortKey | 'user' | 'tier';
  label: string;
  numeric?: boolean;
  sortable?: boolean;
  getValue: (row: RelationshipUserRow) => React.ReactNode;
}

export interface RelationshipScoreTableProps {
  rows: RelationshipUserRow[];
  sort: RelationshipSortKey;
  onSortChange: (key: RelationshipSortKey) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  revealed?: boolean;
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'user',
    label: 'User',
    getValue: (r) => <span className="font-mono text-[11px] text-gray-700">{r.user_token}</span>,
  },
  {
    key: 'relationship_score',
    label: 'Score',
    numeric: true,
    sortable: true,
    getValue: (r) => (
      <span className="font-semibold text-gray-900 tabular-nums">{r.score}</span>
    ),
  },
  {
    key: 'tier',
    label: 'Tier',
    getValue: (r) => <TierBadge tier={r.tier} />,
  },
  {
    key: 'return_days_28d',
    label: 'Return days / 28d',
    numeric: true,
    sortable: true,
    getValue: (r) => (
      <span className="text-gray-700 tabular-nums">{formatInt(r.return_days_28d)}</span>
    ),
  },
  {
    key: 'adherence_pct',
    label: 'Adherence',
    numeric: true,
    sortable: true,
    getValue: (r) => (
      <span className="text-gray-700 tabular-nums">{formatPercent(r.adherence_pct)}</span>
    ),
  },
  {
    key: 'recalibrations_28d',
    label: 'Recalibrations',
    numeric: true,
    sortable: true,
    getValue: (r) => (
      <span className="text-gray-700 tabular-nums">{formatInt(r.recalibrations_28d)}</span>
    ),
  },
  {
    key: 'memories_count',
    label: 'Memories',
    numeric: true,
    sortable: true,
    getValue: (r) => (
      <span className="text-gray-700 tabular-nums">{formatInt(r.memories_count)}</span>
    ),
  },
];

const TierBadge: React.FC<{ tier: string }> = ({ tier }) => (
  <span
    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
    style={{
      backgroundColor: `${RELATIONSHIP_TIER_COLORS[tier] ?? 'var(--text-tertiary)'}20`,
      color: RELATIONSHIP_TIER_COLORS[tier] ?? 'var(--text-strong)',
    }}
  >
    {RELATIONSHIP_TIER_LABEL[tier] ?? tier}
  </span>
);

export const RelationshipScoreTable: React.FC<RelationshipScoreTableProps> = ({
  rows,
  sort,
  onSortChange,
  page,
  pageSize,
  total,
  onPageChange,
  loading = false,
  revealed = false,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {COLUMNS.map((col) => {
                const isActive = col.sortable && sort === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(
                      'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500',
                      col.numeric && 'text-right',
                      col.sortable && 'cursor-pointer select-none hover:text-gray-700',
                    )}
                    onClick={() => col.sortable && onSortChange(col.key as RelationshipSortKey)}
                  >
                    <span className={cn('inline-flex items-center gap-1', col.numeric && 'justify-end w-full')}>
                      {col.label}
                      {col.sortable && isActive && <ChevronDown className="h-3 w-3" />}
                      {col.sortable && !isActive && (
                        <ChevronUp className="h-3 w-3 opacity-0 group-hover:opacity-40" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading &&
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="px-3 py-2">
                      <div className="h-3 w-16 animate-pulse rounded bg-gray-100" />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-xs text-gray-500">
                  No users with activity in this snapshot.
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr key={r.user_token} className="hover:bg-gray-50">
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className={cn('px-3 py-2 align-middle', col.numeric && 'text-right')}
                    >
                      {col.getValue(r)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
        <span>
          {revealed ? 'Identifiers visible' : 'Anonymized'} · {formatInt(total)} total
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Prev
          </button>
          <span className="tabular-nums text-gray-500">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
